import { useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/**
 * Deep links to one record: any page opened with ?focus=<id> scrolls to the
 * element marked data-focus-id="<id>" and highlights it. If that element (or a
 * child) has data-focus-click, it is clicked to open the detail.
 * Used by the inbox (nomad-fest.com/inbox) so "Öffnen" lands exactly on the item.
 */
export function FocusTarget() {
  const location = useLocation();
  useEffect(() => {
    const id = new URLSearchParams(location.search).get('focus');
    if (!id) return;
    let done = false;
    const started = Date.now();
    const tick = () => {
      if (done) return;
      const el = document.querySelector<HTMLElement>(`[data-focus-id="${CSS.escape(id)}"]`);
      if (el) {
        done = true;
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2', 'rounded-lg');
        setTimeout(() => el.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2'), 5000);
        const click = el.matches('[data-focus-click]') ? el : el.querySelector<HTMLElement>('[data-focus-click]');
        if (click) setTimeout(() => click.click(), 400);
        return;
      }
      if (Date.now() - started < 20000) setTimeout(tick, 300);
    };
    tick();
    return () => { done = true; };
  }, [location.pathname, location.search]);
  return null;
}

/** The ?focus id, for lists that need to switch a filter so the item is visible. */
export function useFocusId() {
  const [params] = useSearchParams();
  return params.get('focus');
}
